<template>
  <div class="input-section-wrapper">
    <h3 class="input-section-title">
      Basic information
    </h3>
    <b-row
      class="w-100 margin-bottom-10"
      no-gutters
    >
      <!-- Style Code -->
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group class="input-form-group">
          <template #label>
            <span class="input-label">Style Code *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched, valid }"
            name="Style Code"
            rules="required|min:1|max:100"
          >
            <b-form-input
              v-model="styleCode"
              placeholder="Enter Style Code"
              :class="{ 'is-invalid': touched && errors.length }"
              :state="valid || styleCode === '' ? null : false"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>

      <!-- Parent ID -->
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group class="input-form-group">
          <template #label>
            <span class="input-label">Parent ID *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched, valid }"
            name="Parent ID"
            rules="required|min:1|max:100"
          >
            <b-form-input
              v-model="uniqueId"
              placeholder="Enter Parent ID"
              :class="{ 'is-invalid': touched && errors.length }"
              :state="valid || uniqueId === '' ? null : false"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>

      <!-- Status -->
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group class="input-form-group">
          <template #label>
            <span class="input-label">Status</span>
          </template>
          <div class="d-flex align-items-center status-toggle-wrapper">
            <span
              class="status-label mr-2"
              :class="{ 'text-muted': isLive }"
            >Draft</span>
            <b-form-checkbox
              v-model="isLive"
              switch
              size="lg"
            />
            <span
              class="status-label ml-1"
              :class="{ 'text-muted': !isLive }"
            >Live</span>
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Product Name -->
    <b-row
      class="w-100 margin-bottom-10"
      no-gutters
    >
      <b-col
        md="12"
      >
        <b-form-group class="input-form-group">
          <template #label>
            <span class="input-label">Product Name *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched, valid }"
            name="Product Name"
            rules="required|min:1|max:100"
          >
            <b-form-input
              v-model="name"
              placeholder="Enter Product Name"
              :class="{ 'is-invalid': touched && errors.length }"
              :state="valid || name === '' ? null : false"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row
      class="w-100 margin-bottom-16"
      no-gutters
    >
      <b-col
        md="12"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Product Description</span>
          </template>
          <b-form-textarea
            v-model="descriptionLong"
            placeholder="Enter Product Description"
            rows="3"
            max-rows="3"
            no-resize
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row
      class="w-100 margin-bottom-16"
      no-gutters
    >
      <b-col
        md="12"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Material Composition</span>
          </template>
          <b-form-input
            v-model="descriptionMaterials"
            placeholder="Enter Material Composition"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row
      class="w-100 margin-bottom-16"
      no-gutters
    >
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Department *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched }"
            name="Department"
            rules="required|min:1|max:100"
            :value="department.length === 0 ? '' : department"
          >
            <div>
              <custom-select
                id="department-dropdown"
                v-model="department"
                show-arrow-icon
                placeholder="Select the department"
                no-data-text="No department found."
                theme="grey"
                size="small"
                :is-selection-removable="false"
                :should-scroll-on-focus="true"
                :clear-search-on-select="true"
                :close-on-select="true"
                :option-groups="departmentOptions"
                :class="{ 'is-invalid': touched && errors.length }"
                :add-new-option="true"
                @custom-item="handleAddNewDept"
                @search="(search) => handleSearchDepartment(search)"
                @select="(newDepartment) => department = newDepartment"
              />
              <div class="error-section">
                <small class="text-danger">{{ errors[0] }}</small>
              </div>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Category *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched }"
            name="Category"
            rules="required|min:1|max:100"
            :value="category.length === 0 ? '' : category"
          >
            <custom-select
              id="category-dropdown"
              v-model="category"
              show-arrow-icon
              placeholder="Select the category"
              no-data-text="No category found."
              theme="grey"
              size="small"
              :is-selection-removable="false"
              :clear-search-on-select="true"
              :should-scroll-on-focus="true"
              :close-on-select="true"
              :option-groups="categoryOptions"
              :class="{ 'is-invalid': touched && errors.length }"
              :add-new-option="true"
              @custom-item="handleAddNewCategory"
              @search="(search) => handleSearchCategory(search)"
              @select="(newCategory) => category = newCategory"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
      <b-col
        md="4"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Sub Category *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched }"
            name="Sub Category"
            rules="required|min:1|max:100"
            :value="subCategory.length === 0 ? '' : subCategory"
          >
            <custom-select
              id="sub-category-dropdown"
              v-model="subCategory"
              show-arrow-icon
              placeholder="Select the sub category"
              no-data-text="No sub category found."
              theme="grey"
              size="small"
              :is-selection-removable="false"
              :should-scroll-on-focus="true"
              :close-on-select="true"
              :clear-search-on-select="true"
              :option-groups="subCategoryOptions"
              :is-searchable="true"
              :class="{ 'is-invalid': touched && errors.length }"
              :add-new-option="true"
              @custom-item="handleAddNewSubCategory"
              @search="(search) => handleSearchSubCategory(search)"
              @select="(newSubCategory) => subCategory = newSubCategory"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row
      class="w-100 margin-bottom-16"
      no-gutters
    >
      <b-col
        md="4"
        class="reduce-padding"
      >
        <b-form-group
          class="input-form-group"
        >
          <template #label>
            <span class="input-label">Gender *</span>
          </template>
          <ValidationProvider
            v-slot="{ errors, touched }"
            name="Gender"
            rules="required|min:1|max:100"
            :value="gender.length === 0 ? '' : gender"
          >
            <custom-select
              id="gender-dropdown"
              v-model="gender"
              show-arrow-icon
              placeholder="Select the gender"
              no-data-text="No gender found."
              theme="grey"
              size="small"
              :is-searchable="true"
              :is-selection-removable="false"
              :should-scroll-on-focus="true"
              :close-on-select="true"
              :option-groups="genderOptions"
              :add-new-option="true"
              :clear-search-on-select="true"
              :class="{ 'is-invalid': touched && errors.length }"
              @custom-item="handleAddNewGender"
              @search="(search) => handleSearchGender(search)"
              @select="(newGender) => gender = newGender"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </ValidationProvider>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import {
  BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox,
} from 'bootstrap-vue'
import CustomSelect from '@/@core/components/custom-select/CustomSelect.vue'
import { ValidationProvider } from 'vee-validate'
import { productConstants } from '@kingpin-global/kingpin-utils-frontend'
import { useBasicInfo } from './hooks/useBasicInfo'

export default {
  name: 'BasicInformation',
  components: {
    BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, CustomSelect, ValidationProvider,
  },
  data() {
    return {
      categoryOptions: [
        {
          title: '',
          options: productConstants.CATEGORIES,
        },
      ],
      departmentOptions: [
        {
          title: '',
          options: productConstants.DEPARTMENTS,
        },
      ],
      genderOptions: [
        {
          title: '',
          options: productConstants.GENDER,
        },
      ],
    }
  },
  setup() {
    const {
      subCategory,
      subCategoryOptions,
      category,
      department,
      gender,
      status,
      isLive,
      descriptionLong,
      descriptionMaterials,
      uniqueId,
      name,
      styleCode,
    } = useBasicInfo()
    return {
      subCategory,
      subCategoryOptions,
      category,
      department,
      gender,
      status,
      isLive,
      descriptionLong,
      descriptionMaterials,
      uniqueId,
      name,
      styleCode,
    }
  },
  methods: {
    handleSearchDepartment(search) {
      this.departmentOptions = [
        {
          title: '',
          options: productConstants.DEPARTMENTS.filter(department => department.toLowerCase().includes(search.toLowerCase())),
        },
      ]
    },
    handleSearchCategory(search) {
      this.categoryOptions = [
        {
          title: '',
          options: productConstants.CATEGORIES.filter(category => category.toLowerCase().includes(search.toLowerCase())),
        },
      ]
    },
    handleSearchSubCategory(search) {
      const allOptions = productConstants.SUB_CATEGORIES[this.category[0]] || []
      this.subCategoryOptions = [
        {
          title: '',
          options: allOptions.filter(subCategory => subCategory.toLowerCase().includes(search.toLowerCase())),
        },
      ]
    },
    handleSearchGender(search) {
      this.genderOptions = [
        {
          title: '',
          options: productConstants.GENDER.filter(gender => gender.toLowerCase().includes(search.toLowerCase())),
        },
      ]
    },
    handleAddNewDept(newItem) {
      this.department = [newItem]
      this.departmentOptions = [
        {
          title: '',
          options: [...productConstants.DEPARTMENTS, newItem],
        },
      ]
    },
    handleAddNewCategory(newItem) {
      this.category = [newItem]
      this.categoryOptions = [
        {
          title: '',
          options: [...productConstants.CATEGORIES, newItem],
        },
      ]
    },
    handleAddNewSubCategory(newItem) {
      this.subCategory = [newItem]
      this.subCategoryOptions = [
        {
          title: '',
          options: [...productConstants.SUB_CATEGORIES[this.category[0]], newItem],
        },
      ]
    },
    handleAddNewGender(newItem) {
      this.gender = [newItem]
      this.genderOptions = [
        {
          title: '',
          options: [...productConstants.GENDER, newItem],
        },
      ]
    },
  },
}
</script>
<style lang="scss" scoped>
.status-toggle-wrapper {
  height: 38px;

  .status-label {
    font-size: 14px;
    font-weight: 500;

    &.text-muted {
      color: #b9b9c3;
    }
  }
}
</style>
